/**
 * Page metrics shared by Forbered udskrift / PDF (`PAGE_CSS`) and Word/RTF export.
 * `@page { margin: 16mm 12mm 18mm 12mm }` on A4 portrait.
 */

export const FONT_SANS = "IBM Plex Sans";
export const FONT_SANS_FALLBACK = "Calibri";

export const INK = "1A1A1A";
export const MUTED = "6B6560";
export const ACCENT = "2C5F4A";
export const BORDER = "D4CCC0";
export const RULE = "1A1A1A";
export const PAPER = "FAF7F0";
export const DEFAULT_SCENE_HEADER_BG = "DCE8E2";

/** A4 portrait in millimetres. */
export const PAGE_WIDTH_MM = 210;
export const PAGE_HEIGHT_MM = 297;
export const MARGIN_TOP_MM = 16;
export const MARGIN_RIGHT_MM = 12;
export const MARGIN_BOTTOM_MM = 18;
export const MARGIN_LEFT_MM = 12;
/** Header sits in the top margin band (pagedjs running head). */
export const HEADER_FROM_EDGE_MM = 8;
export const FOOTER_FROM_EDGE_MM = 10;

/**
 * Body is `--sm-ms-size: 0.95rem` ≈ 11.4 pt. Half-points for RTF `\fs` / docx `size`.
 */
export const FS_BODY = 22; // 11 pt
export const FS_SCENE_TITLE = 26; // ~13 pt (1.05rem)
export const FS_SCENE_NUM = 20; // 10 pt (0.8rem)
export const FS_COL_HEAD = 16; // 8 pt (0.7rem)
export const FS_HEADER = 16; // 8 pt running head
export const FS_CHARACTER = 20; // 10 pt
export const FS_META = 18; // 9 pt
export const FS_DURATION = 20; // 10 pt

/** Line spacing 1.45 × 240 (Word auto line units). */
export const LINE_240 = 348;

/** Paragraphs in a cell share the same line box as wrapped lines / Shift+Return. */
export const PARA_GAP_TWIP = 0;

/** Cell padding: 0.4rem / 0.45rem ≈ 96 / 108 twips. */
export const CELL_PAD_X = 96;
export const CELL_PAD_Y = 108;

export function mmToTwip(mm: number): number {
  return Math.round((mm * 1440) / 25.4);
}

export const PAGE_W_TWIP = mmToTwip(PAGE_WIDTH_MM);
export const PAGE_H_TWIP = mmToTwip(PAGE_HEIGHT_MM);
export const MARGIN_TOP_TWIP = mmToTwip(MARGIN_TOP_MM);
export const MARGIN_RIGHT_TWIP = mmToTwip(MARGIN_RIGHT_MM);
export const MARGIN_BOTTOM_TWIP = mmToTwip(MARGIN_BOTTOM_MM);
export const MARGIN_LEFT_TWIP = mmToTwip(MARGIN_LEFT_MM);
export const HEADER_TWIP = mmToTwip(HEADER_FROM_EDGE_MM);
export const FOOTER_TWIP = mmToTwip(FOOTER_FROM_EDGE_MM);

export const CONTENT_W_TWIP =
  PAGE_W_TWIP - MARGIN_LEFT_TWIP - MARGIN_RIGHT_TWIP;

/** Duration column ≈ 6.75rem on Sceneliste / scene heads. */
export const DURATION_COL_TWIP = mmToTwip(28);

export function hexRgb(hex: string): { r: number; g: number; b: number } | null {
  const raw = hex.trim().replace(/^#/, "");
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: Number.parseInt(full.slice(0, 2), 16),
    g: Number.parseInt(full.slice(2, 4), 16),
    b: Number.parseInt(full.slice(4, 6), 16),
  };
}

export function normalizeHex(hex: string | undefined | null, fallback = DEFAULT_SCENE_HEADER_BG): string {
  const rgb = hexRgb(hex ?? "");
  if (!rgb) return fallback.replace(/^#/, "").toUpperCase();
  const h = (n: number) => n.toString(16).padStart(2, "0");
  return `${h(rgb.r)}${h(rgb.g)}${h(rgb.b)}`.toUpperCase();
}
